import React from "react";
import { FormikProps } from "formik";
import classnames from "classnames";

import { removeNonNumeric } from "@vahak/core-ui/dist/methods/string";

import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";

import { LoginFormFieldsTypes, loginFormFieldNames } from "../../login-form-new/common/contant";
import { LOGIN_PHONE_NUMBER_LENGTH } from "../../login-form-new/hooks/auth";
import useCompactLoadPostingTranslation from "../compact-load-posting/hooks/useCompactLoadPostingTranslation";
import { TrackEventHandler } from "../../../types/events";
import { useTrackWebV2FieldOnce } from "../analytics/homepageV2WebAnalytics";
import { useCompactSheetAutoFocus } from "../hooks/useCompactSheetAutoFocus";
import { createCompactSheetInputFocusHandler } from "../methods/homepageV2CompactSheet";

import Styles from "./CompactPhoneNumberField.module.scss";

const COMPACT_LOGIN_PHONE_INPUT_ID = "compact-login-phone";
const handleAuthFocus = createCompactSheetInputFocusHandler("auth");

interface CompactPhoneNumberFieldProps {
    formik: FormikProps<LoginFormFieldsTypes>;
    variant?: "default" | "bid";
    layout?: "default" | "inline";
    trackEvent?: TrackEventHandler;
}

const CompactPhoneNumberField = ({
    formik,
    variant = "default",
    layout = "default",
    trackEvent
}: CompactPhoneNumberFieldProps) => {
    const { t } = useCompactLoadPostingTranslation();
    const { trackFieldOnce } = useTrackWebV2FieldOnce(trackEvent, {
        source_screen: WEB_V2_SOURCE_SCREENS.ONB_FORM
    });
    const phoneValue = String(formik.values.phoneNumber || "");
    const hasError = Boolean(formik.touched.phoneNumber && formik.errors.phoneNumber);
    useCompactSheetAutoFocus(COMPACT_LOGIN_PHONE_INPUT_ID);

    const onChangePhoneNum: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.target.value = removeNonNumeric(e.target.value.slice(0, LOGIN_PHONE_NUMBER_LENGTH));
        formik.handleChange(loginFormFieldNames.phoneNumber)(e);

        if (e.target.value.length >= 1) {
            trackFieldOnce("phone", GA4EventNames.WEB_V2.phone_number_entered, {
                Number: e.target.value
            });
        }
    };

    return (
        <div className={classnames(Styles.field, layout === "inline" && Styles.fieldInline)}>
            <label className={Styles.label} htmlFor="compact-login-phone">
                {t("load-form:login.mobileLabel")}
            </label>
            <div className={Styles.inputRow}>
                <div className={classnames(Styles.countryCode, variant === "bid" && Styles.countryCodeBid)} aria-hidden>
                    {layout === "inline" ? null : <span className={Styles.flag}>🇮🇳</span>}
                    <span className={Styles.dialCode}>+91</span>
                </div>
                <input
                    id={COMPACT_LOGIN_PHONE_INPUT_ID}
                    className={classnames(Styles.phoneInput, variant === "bid" && Styles.phoneInputBid, {
                        [Styles.phoneInputError]: hasError && layout !== "inline"
                    })}
                    type="tel"
                    inputMode="numeric"
                    name={loginFormFieldNames.phoneNumber}
                    value={phoneValue}
                    onChange={onChangePhoneNum}
                    onFocus={handleAuthFocus}
                    onBlur={formik.handleBlur}
                    placeholder="98765 43210"
                    autoComplete="tel-national"
                />
            </div>
            {hasError && layout !== "inline" ? <p className={Styles.fieldError}>{formik.errors.phoneNumber}</p> : null}
        </div>
    );
};

export default CompactPhoneNumberField;
